import { createContext, useContext, FC, ReactNode, useEffect } from 'react'
import { useAsyncRetry } from 'react-use'
import { crmApi } from '@/api'
import { LeadStatus } from '@/api/types/crm.types'
import { useAddAlert } from '@/hooks/useAddAlert'

interface LeadStatusContextType {
  statuses: LeadStatus[]
  loading: boolean
  refreshStatuses: () => void
}

const LeadStatusContext = createContext<LeadStatusContextType | undefined>(
  undefined,
)

interface LeadStatusProviderProps {
  leadId: number
  children: ReactNode
  onNeedUpdateLead?: () => void
}

export const LeadStatusProvider: FC<LeadStatusProviderProps> = ({
  leadId,
  children,
  onNeedUpdateLead,
}) => {
  const addAlert = useAddAlert()

  const state = useAsyncRetry(
    async () => await crmApi.getAllLeadStatuses({ lead_id: leadId }),
    [leadId],
  )

  useEffect(() => {
    if (state.error) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось загрузить статусы заявки',
        type: 'error',
      })
    }
  }, [state.error])

  const handlerRefetchStatuses = () => {
    state.retry()
    onNeedUpdateLead && onNeedUpdateLead()
  }

  return (
    <LeadStatusContext.Provider
      value={{
        statuses: state.value?.data?.data || [],
        loading: state.loading,
        refreshStatuses: handlerRefetchStatuses,
      }}
    >
      {children}
    </LeadStatusContext.Provider>
  )
}

export const useLeadStatuses = () => {
  const context = useContext(LeadStatusContext)
  if (!context) {
    throw new Error('useLeadStatuses must be used within a LeadStatusProvider')
  }
  return context
}
